import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {
  DSM3DropdownControlComponent,
  DSM3DatePickerControlAltComponent,
  DSM3RadioDropdownControlComponent,
  DSM3SelectOption,
} from '@eredes/dsm3';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

import { categoriesTypes } from '../models/request-type.model';
import { RequestService } from '../request.service';

import { UserService } from 'app/_core/services/user.service';

@Component({
  selector: 'app-request-filters',
  templateUrl: './request-filters.component.html',
  styleUrls: ['./request-filters.component.scss'],
  standalone: false,
})
export class RequestFiltersComponent implements OnInit {
  @Input() formGroup: UntypedFormGroup;
  @Output() valueChanges = new EventEmitter<any>();

  @ViewChild('date') datePickerComponent: DSM3DatePickerControlAltComponent;
  @ViewChild('areas') areasControl: DSM3RadioDropdownControlComponent;
  @ViewChild('categories') categoriesControl: DSM3RadioDropdownControlComponent;

  @ViewChild(DSM3DropdownControlComponent) dropdownComponents: DSM3DropdownControlComponent;
  @ViewChild(DSM3RadioDropdownControlComponent) radioComponent: DSM3RadioDropdownControlComponent;

  statusOptions$ = new BehaviorSubject<DSM3SelectOption[]>([]);
  categoriesOptions$ = new BehaviorSubject<DSM3SelectOption[]>([]);
  areasOptions$ = new BehaviorSubject<DSM3SelectOption[]>([]);
  isRetailer: boolean;
  requestOperationData: DSM3SelectOption[] = categoriesTypes;
  currentYear: number;

  constructor(
    protected userService: UserService,
    protected requestService: RequestService
  ) {}

  ngOnInit(): void {
    this.isRetailer = this.userService.isProfileRetailer();
    this.initData();
  }

  /**
   * Sets filters default data
   */
  private initData() {
    this.currentYear = new Date().getFullYear();

    this.getAllStatus();
    this.categoriesOptions$.next(this.requestOperationData);

    this.formGroup.get('category').valueChanges.subscribe((value) => {
      this.onValueChange();
    });

    this.formGroup.get('area').valueChanges.subscribe((value) => {
      this.onValueChange();
    });
  }

  /**
   * Get all areas
   * @param typeOperation
   * @returns Areas labels and values
   */
  getAllAreas(typeOperation?: string) {
    this.areasOptions$.next([]);
    if (typeOperation !== '') {
      const areasOptions$ = this.requestService.getOperationChildren(typeOperation);
      areasOptions$
        .pipe(
          map((areasOptions) => {
            const p = [];
            areasOptions.Result.Collection.forEach((result) => {
              p.push({
                label: result.requestOperationTitle,
                value: result.requestOperationUniqueCode,
              });
            });

            return p;
          })
        )
        .subscribe((v) => this.areasOptions$.next(v));
    } else {
      this.areasOptions$.next([]);
    }
  }

  /**
   * Get all status
   * @returns Status labels and values
   */
  getAllStatus() {
    this.requestService
      .getInitialData(this.isRetailer)
      .pipe(
        map((statusOptions) => {
          const p = [];
          statusOptions[0].Result.Collection.forEach((result) => {
            p.push({
              label: result.requestStatusTitle,
              value: result.requestStatusUniqueCode,
            });
          });

          return p;
        })
      )
      .subscribe((res) => {
        this.statusOptions$.next(res);
      });
  }

  /**
   * Closes dropdownRadioComponent and dropdownComponents
   */
  onOpenDatepicker(): void {
    this.radioComponent.close();
    this.dropdownComponents.close();
  }

  /**
   * Closes dropdownRadioComponent, datePickerComponent and areasComponent
   */
  onOpenDropdowns(): void {
    this.radioComponent.close();
    this.datePickerComponent.openDatePicker = false;
  }

  /**
   * Closes dropdownRadioComponent, dropdownComponents and datePickerComponent
   */
  onFocusSearch(): void {
    this.radioComponent.close();
    this.dropdownComponents.close();
    this.datePickerComponent.openDatePicker = false;
  }

  /**
   * Closes dropdownComponents, datePickerComponent and categoriesDropdownRadio
   */
  onOpenDropdownRadioAreas(): void {
    this.dropdownComponents.close();
    this.datePickerComponent.openDatePicker = false;
    this.categoriesControl.close();
  }

  /**
   * Closes dropdownComponents, datePickerComponent and areasDropdownRadio
   */
  onOpenDropdownRadioCategories(): void {
    this.dropdownComponents.close();
    this.datePickerComponent.openDatePicker = false;
    this.areasControl.close();
  }

  /**
   * The function emits for the parent that the form change
   */
  onValueChange(): void {
    this.valueChanges.emit();
  }

  /**
   * It returns the form control for the input form date
   */
  get getInputFormDateControl(): UntypedFormControl {
    return this.formGroup.get('date') as UntypedFormControl;
  }
}
